import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Wand2, Heart, Trophy, X, ArrowRight, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'zenscroll_onboardingSeen_v1';

const STEPS = [
  {
    Icon: Compass,
    title: 'Welcome to ZenScroll',
    body: 'A calmer kind of feed — quotes, facts, stories, videos and community art interleaved by mindful category so nothing ever feels like doom-scrolling.',
  },
  {
    Icon: Wand2,
    title: 'Create anything you can imagine',
    body: 'Tap Create to spin up AI images and videos from any prompt. Want to riff on someone else\'s post? Use Meme this to remix it in one tap.',
  },
  {
    Icon: Trophy,
    title: 'Land on Trending with the Daily Challenge',
    body: 'Every day we drop a new mindful prompt on the home page. Join in to be featured on the Trending grid and earn streak badges.',
  },
  {
    Icon: Heart,
    title: 'Save, share & shape your feed',
    body: 'Heart what lands. Open any post in full-screen to Save it to your device, Share it with others, or Unmute the audio on videos. Your Z menu has everything else.',
  },
];

export default function OnboardingTutorial({ token }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!token) return;
    let seen = '';
    try { seen = localStorage.getItem(STORAGE_KEY) || ''; } catch { /* ignore */ }
    if (!seen) {
      // Slight delay so it doesn't fight the welcome render.
      const t = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [token]);

  const close = (markSeen = true) => {
    if (markSeen) {
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    }
    setOpen(false);
  };

  const finish = () => {
    close(true);
    navigate('/zenflow');
  };

  if (!open) return null;
  const { Icon, title, body } = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] bg-black/75 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
        data-testid="onboarding-overlay"
        onClick={() => close(true)}
      >
        <motion.div
          key={step}
          initial={{ y: 24, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 24, opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md bg-[#101F1D] border border-white/10 rounded-3xl p-7 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        >
          <button
            onClick={() => close(true)}
            data-testid="onboarding-skip"
            aria-label="Skip onboarding"
            className="absolute top-4 right-4 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#1C4749]/40 text-[#FFD166] mb-5">
            <Icon className="w-5 h-5" />
          </span>

          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FFD166]">
            Step {step + 1} of {STEPS.length}
          </p>
          <h3 className="mt-2 text-2xl text-white tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {title}
          </h3>
          <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{body}</p>

          {/* Dots */}
          <div className="mt-6 flex items-center gap-1.5" data-testid="onboarding-dots">
            {STEPS.map((s, i) => (
              <span
                key={`onboard-dot-${s.title || i}`}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-8 bg-[#FFD166]' : 'w-1.5 bg-white/20'}`}
              />
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={() => close(true)}
              data-testid="onboarding-skip-link"
              className="text-xs text-white/50 hover:text-white/80 transition-colors"
            >
              Skip the tour
            </button>
            {isLast ? (
              <button
                onClick={finish}
                data-testid="onboarding-finish"
                className="inline-flex items-center gap-2 bg-[#1C4749] hover:bg-[#2A6F72] text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-colors shadow-[0_0_18px_rgba(28,71,73,0.4)]"
              >
                <Sparkles className="w-4 h-4" /> Take me to Zenflow
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                data-testid="onboarding-next"
                className="inline-flex items-center gap-2 bg-[#1C4749] hover:bg-[#2A6F72] text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-colors shadow-[0_0_18px_rgba(28,71,73,0.4)]"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
